import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
    alpha,
    AppBar,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    FormControlLabel,
    FormLabel,
    IconButton,
    Radio,
    RadioGroup,
    Slide,
    TextField,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { TransitionProps } from '@mui/material/transitions';
import { Theme } from '@mui/material/styles';
import { format } from 'date-fns';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as yup from 'yup';
import { IExpandedTourDate } from '../../models/IExpandedTourDate';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<unknown>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface ReservationModalProps {
    open: boolean;
    onClose: () => void;
    tourDate: IExpandedTourDate | null;
}

interface ReservationFormData {
    firstName: string;
    lastName: string;
    address: string;
    email: string;
    phone: string;
    usesOwnMotorcycle: boolean;
    transferOption: "none" | "sarajevo" | "split";
    acceptTermsOfBusiness: boolean;
}
const TEXT_FIELD_ACCENT = "#376ac7";

const ReservationModal: React.FC<ReservationModalProps> = ({ open, onClose, tourDate }) => {
    const { t } = useTranslation();
    const schema = React.useMemo(() => yup.object({
        firstName: yup.string().required(t('reservationModal.validation.firstNameRequired')),
        lastName: yup.string().required(t('reservationModal.validation.lastNameRequired')),
        address: yup.string().required(t('reservationModal.validation.addressRequired')),
        email: yup.string().email(t('reservationModal.validation.emailInvalid')).required(t('reservationModal.validation.emailRequired')),
        phone: yup.string().required(t('reservationModal.validation.phoneRequired')),
        usesOwnMotorcycle: yup.boolean().required(),
        transferOption: yup.mixed<ReservationFormData["transferOption"]>().oneOf(["none", "sarajevo", "split"]).required(),
        acceptTermsOfBusiness: yup.boolean().oneOf([true], t('reservationModal.validation.termsRequired')).required(),
    }) as yup.ObjectSchema<ReservationFormData>, [t]);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch
    } = useForm<ReservationFormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            usesOwnMotorcycle: false,
            transferOption: "none",
            acceptTermsOfBusiness: false,
        }
    });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const usesOwnMotorcycle = watch('usesOwnMotorcycle');
    const transferOption = watch('transferOption');
    const selectedPrice = usesOwnMotorcycle
        ? tourDate?.tour.priceWithOwnMotorcycle
        : tourDate?.tour.price;
    const transferPrice = transferOption === "sarajevo"
        ? 70
        : transferOption === "split"
            ? 80
            : 0;
    const totalPrice = (selectedPrice ?? 0) + transferPrice;
    const priceBreakdown = transferPrice > 0
        ? `(${selectedPrice ?? 0} + ${transferPrice})`
        : '';

    const transferText =
        transferOption === "sarajevo"
            ? t('reservationModal.transferOptions.sarajevo')
            : transferOption === "split"
                ? t('reservationModal.transferOptions.split')
                : t('reservationModal.transferOptions.none');

    const onSubmit = (data: ReservationFormData) => {
        const message = `
${t('reservationModal.message.newReservation')}:

${t('reservationModal.message.tour')}: ${tourDate?.tour.name ? t(tourDate.tour.name) : ''}
${t('reservationModal.message.date')}: ${format(new Date(tourDate?.startDate!), 'dd.MM.yyyy')} – ${format(new Date(tourDate?.endDate!), 'dd.MM.yyyy')}
${t('reservationModal.message.price')}: ${totalPrice} € ${priceBreakdown}
${t('reservationModal.message.motorcycle')}: ${usesOwnMotorcycle ? t('reservationModal.message.motorcycleOwn') : t('reservationModal.message.motorcycleOur')}
${t('reservationModal.message.transfer')}: ${transferText}

${t('reservationModal.message.name')}: ${data.firstName} ${data.lastName}
${t('reservationModal.message.address')}: ${data.address}
${t('reservationModal.message.email')}: ${data.email}
${t('reservationModal.message.phone')}: ${data.phone}
`;

        const phoneNumber = "38763262122";
        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
        reset();
        onClose();
    };

    const reservationTextFieldSx = (withMarginBottom: boolean) => (theme: Theme) => ({
        ...(withMarginBottom ? { mb: 2 } : {}),
        "& .MuiInputLabel-root": {
            color: TEXT_FIELD_ACCENT,
            "&.Mui-focused": {
                color: TEXT_FIELD_ACCENT,
            },
        },
        "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.default, 0.6),
            "& fieldset": {
                borderColor: alpha(theme.palette.text.primary, 0.2),
            },
            "&:hover fieldset": {
                borderColor: alpha(theme.palette.text.primary, 0.35),
            },
            "&.Mui-focused fieldset": {
                borderColor: TEXT_FIELD_ACCENT,
                boxShadow: `0 0 0 3px ${alpha(TEXT_FIELD_ACCENT, 0.2)}`,
            },
        },
        "& .MuiInputBase-input": {
            color: theme.palette.common.white,
        },
    });

    return (
        <Dialog
            fullScreen={isMobile ? true : false}
            fullWidth={!isMobile ? true : false}
            open={open}
            maxWidth="sm"
            onClose={onClose}
            TransitionComponent={Transition}>
            <AppBar
                sx={(theme) => ({
                    position: 'relative',
                    backgroundColor: grey[50],
                    pt: "6px",
                    pb: "8px",
                    ...theme.applyStyles("dark", {
                        boxShadow: "none",
                        borderBottom: "1px solid #494949ff",
                        backgroundColor: theme.palette.background.default
                    })
                })}>
                <Toolbar>
                    <Box display={"flex"} flexDirection={"column"} flexGrow={1}>
                        {tourDate && (
                            <>
                                <Typography ml={"2px"} variant="h6" fontWeight={600} color={"text2.primary"}>🏞 {t(tourDate?.tour.name)}</Typography>
                                <Typography variant='subtitle2' fontWeight={600} color={"text2.primary"}>
                                    📅 {format(new Date(tourDate.startDate), 'dd.MM.yyyy')} – {format(new Date(tourDate.endDate), 'dd.MM.yyyy')}
                                </Typography>
                                <Typography fontWeight={600} color="secondary.main">
                                    🪙 {totalPrice} € {priceBreakdown}
                                </Typography>
                            </>
                        )}

                    </Box>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon color='action' />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <DialogContent sx={{ px: { xs: 2, sm: 3 }, backgroundColor: (theme) => theme.palette.background.default }}>
                <Box component="form" onSubmit={handleSubmit(onSubmit)} display="flex" flexDirection="column" gap={2} mt={2}>
                    <Box
                        sx={(theme) => ({
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                        })}
                    >
                        <FormLabel sx={{ mb: 1, display: "block" }}>{t('reservationModal.motorSection.title')}</FormLabel>
                        <Controller
                            name="usesOwnMotorcycle"
                            control={control}
                            render={({ field }) => (
                                <Box display="flex" gap={1}>
                                    <Box
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => field.onChange(false)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                field.onChange(false);
                                            }
                                        }}
                                        sx={(theme) => ({
                                            flex: "1 1 50%",
                                            p: 1.5,
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.divider}`,
                                            textAlign: "center",
                                            cursor: "pointer",
                                            backgroundColor: field.value
                                                ? theme.palette.background.default
                                                : alpha(theme.palette.secondary.main, 0.12),
                                            borderColor: field.value
                                                ? theme.palette.divider
                                                : theme.palette.secondary.main,
                                            boxShadow: field.value
                                                ? "none"
                                                : `0 0 0 2px ${alpha(theme.palette.secondary.main, 0.15)}`,
                                            outline: "none",
                                            WebkitTapHighlightColor: "transparent",
                                        })}
                                    >
                                        <Typography variant="body2" fontWeight={700}>
                                            {t('reservationModal.motorSection.ourMotor')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {tourDate?.tour.price} €
                                        </Typography>
                                    </Box>
                                    <Box
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => field.onChange(true)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                field.onChange(true);
                                            }
                                        }}
                                        sx={(theme) => ({
                                            flex: "1 1 50%",
                                            p: 1.5,
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.divider}`,
                                            textAlign: "center",
                                            cursor: "pointer",
                                            backgroundColor: field.value
                                                ? alpha(theme.palette.secondary.main, 0.12)
                                                : theme.palette.background.default,
                                            borderColor: field.value
                                                ? theme.palette.secondary.main
                                                : theme.palette.divider,
                                            boxShadow: field.value
                                                ? `0 0 0 2px ${alpha(theme.palette.secondary.main, 0.15)}`
                                                : "none",
                                            outline: "none",
                                            WebkitTapHighlightColor: "transparent",
                                        })}
                                    >
                                        <Typography variant="body2" fontWeight={700}>
                                            {t('reservationModal.motorSection.ownMotor')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {tourDate?.tour.priceWithOwnMotorcycle} €
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        />
                        {!tourDate && (
                            <Typography variant="caption" color="text.secondary">
                                {t('reservationModal.motorSection.pricesAfterSelection')}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={(theme) => ({
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                        })}
                    >
                        <FormLabel sx={{ mb: 1, display: "block" }}>{t('reservationModal.transferSection.title')}</FormLabel>
                        <Controller
                            name="transferOption"
                            control={control}
                            render={({ field }) => (
                                <RadioGroup
                                    value={field.value}
                                    onChange={(event) => field.onChange(event.target.value)}
                                    sx={{
                                        gap: 1
                                    }}
                                >
                                    <FormControlLabel
                                        value="sarajevo"
                                        control={<Radio color="secondary" />}
                                        label={t('reservationModal.transferOptions.sarajevo')}
                                    />
                                    <FormControlLabel
                                        value="split"
                                        control={<Radio color="secondary" />}
                                        label={t('reservationModal.transferOptions.split')}
                                    />
                                    <FormControlLabel
                                        value="none"
                                        control={<Radio color="secondary" />}
                                        label={t('reservationModal.transferOptions.none')}
                                    />
                                </RadioGroup>
                            )}
                        />
                    </Box>
                    <Box
                        sx={(theme) => ({
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                        })}
                    >
                        <Typography variant="subtitle1" fontWeight={700} mb={1}>
                            {t('reservationModal.personalData.title')}
                        </Typography>
                        <TextField
                            label={t('reservationModal.fields.firstName')}
                            fullWidth
                            {...register('firstName')}
                            error={!!errors.firstName}
                            helperText={errors.firstName?.message}
                            sx={reservationTextFieldSx(true)}
                        />
                        <TextField
                            label={t('reservationModal.fields.lastName')}
                            fullWidth
                            {...register('lastName')}
                            error={!!errors.lastName}
                            helperText={errors.lastName?.message}
                            sx={reservationTextFieldSx(true)}
                        />
                        <TextField
                            label={t('reservationModal.fields.address')}
                            fullWidth
                            {...register('address')}
                            error={!!errors.address}
                            helperText={errors.address?.message}
                            sx={reservationTextFieldSx(true)}
                        />
                        <TextField
                            label={t('reservationModal.fields.email')}
                            type="email"
                            fullWidth
                            {...register('email')}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            sx={reservationTextFieldSx(true)}
                        />
                        <TextField
                            label={t('reservationModal.fields.phone')}
                            fullWidth
                            {...register('phone')}
                            error={!!errors.phone}
                            helperText={errors.phone?.message}
                            sx={reservationTextFieldSx(false)}
                        />
                    </Box>

                    <Box
                        sx={(theme) => ({
                            borderLeft: '4px solid',
                            borderColor: theme.palette.primary.main,
                            pl: 2,
                            py: 1,
                            pr: 1,
                        })}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('reservationModal.depositNote')}
                        </Typography>
                        <Controller
                            name="acceptTermsOfBusiness"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                            color="secondary"
                                        />
                                    }
                                    label={
                                        <Typography component="span" variant="body2">
                                            {t('reservationModal.termsAccept')}{' '}
                                            <Link
                                                to="/terms-of-business"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {t('reservationModal.termsLink')}
                                            </Link>
                                        </Typography>
                                    }
                                    sx={{ alignItems: 'center' }}
                                />
                            )}
                        />
                        {errors.acceptTermsOfBusiness && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                {errors.acceptTermsOfBusiness.message}
                            </Typography>
                        )}
                    </Box>

                    <Button type="submit" size='large' variant="contained" color='secondary' sx={{ mt: 2, p: "14px 8px" }}>
                        {t('reservationModal.submit')}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ReservationModal;
